/**
 * The fields the printed ID card uses, mirrored from the superadmin panel so
 * the public form and the office's edit screen ask for exactly the same things
 * with exactly the same rules.
 *
 * Lengths match the `student` table columns, so nothing the student types can
 * be truncated later when the office promotes a correction to the master
 * record. See ID_CARD_REQUEST_PLAN.md §0.
 */

export const CARD_FIELDS = [
  { name: "name", label: "Full Name", maxLength: 200 },
  { name: "father_name", label: "Father's Name", maxLength: 100 },
  { name: "roll_no", label: "Roll Number", maxLength: 50 },
  { name: "email", label: "Email", maxLength: 50, type: "email" },
  { name: "phone", label: "Contact Number", maxLength: 50 },
  { name: "course_code", label: "Course", control: "course" },
  { name: "term", label: "Semester / Year", control: "term" },
  { name: "date_of_birth", label: "Date of Birth", control: "date" },
  { name: "village", label: "Village / Address", maxLength: 200 },
  { name: "post_office", label: "Post Office", maxLength: 100 },
  { name: "pin", label: "PIN Code", maxLength: 20 },
  { name: "district", label: "District", maxLength: 100 },
  { name: "state", label: "State", maxLength: 100 }
];

export const TERM_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

export const DEFAULT_STATE = "Assam";

/**
 * The photo is not a CARD_FIELDS entry: it is an array of master_file ids
 * rather than a string, it has its own upload control, and the prefill lookup
 * has nothing to prefill it with. It is carried on the same form object so the
 * submit payload stays a single flat body.
 */
export const PHOTO_FIELD = "photo_file";

/** Max upload size for the photo, in KB. Passed straight to FileUpload. */
export const PHOTO_LIMIT_KB = 300;

export function emptyCardForm() {
  let form = {};
  CARD_FIELDS.forEach((f) => {
    form[f.name] = "";
  });
  form.state = DEFAULT_STATE;
  form[PHOTO_FIELD] = [];
  return form;
}

export function hasPhoto(form) {
  return Array.isArray(form[PHOTO_FIELD]) && form[PHOTO_FIELD].length > 0;
}

/** Card fields still blank. term = 0 counts as blank — it prints nothing useful. */
export function missingCardFields(form) {
  return CARD_FIELDS.filter((f) => {
    let value = form[f.name];
    if (f.name === "term") {
      return !value || parseInt(value, 10) < 1;
    }
    return value === undefined || value === null || String(value).trim() === "";
  });
}

/**
 * Same rules as the card renderer, so the student can see the exact Class line
 * that will be printed.
 */
export function getGoodClass(course_code, term) {
  if (!course_code) {
    return "";
  }

  const t = parseInt(term, 10);
  const isHigherSecondary =
    course_code === "HSA" || course_code === "HSS" || course_code === "HSC";

  if (!t || t < 1) {
    return course_code;
  }

  if (isHigherSecondary) {
    if (t === 1) return course_code + " 1st Year";
    if (t === 2) return course_code + " 2nd Year";
    return `${course_code} Year ${t}`;
  }

  if (t === 1) return course_code + " 1st Semester";
  if (t === 2) return course_code + " 2nd Semester";
  if (t === 3) return course_code + " 3rd Semester";
  return `${course_code} ${t}th Semester`;
}
